import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Slider,
  Tooltip
} from '@mui/material';
import {
  SwapHoriz,
  Close,
  SwapVert,
  Info,
  Settings,
  AccountBalanceWallet,
  ArrowDownward
} from '@mui/icons-material';

const SwapDialog = ({ open, onClose, wallet, onSwap }) => {
  const [fromToken, setFromToken] = useState('ETH');
  const [toToken, setToToken] = useState('USDT');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState(0.5);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [step, setStep] = useState(1); // 1: Form, 2: Confirmation, 3: Success

  // Available tokens based on the wallet's network
  const availableTokens = wallet?.network?.includes('Ethereum') 
    ? ['ETH', 'USDT', 'USDC', 'DAI', 'WBTC', 'UNI', 'LINK'] 
    : wallet?.network?.includes('Solana')
      ? ['SOL', 'USDC', 'RAY', 'SRM', 'ORCA']
      : wallet?.network?.includes('TON')
        ? ['TON', 'JETTON', 'BOLT']
        : ['ETH', 'USDT', 'USDC', 'DAI'];

  // Mock token balances
  const tokenBalances = {
    ETH: '1.5',
    USDT: '500',
    USDC: '500',
    DAI: '500',
    WBTC: '0.05',
    UNI: '50',
    LINK: '100',
    SOL: '10',
    RAY: '100',
    SRM: '200',
    ORCA: '150',
    TON: '100',
    JETTON: '1000',
    BOLT: '2000'
  };

  // Mock exchange rates
  const exchangeRates = {
    'ETH-USDT': 3000,
    'ETH-USDC': 3000,
    'ETH-DAI': 3000,
    'ETH-WBTC': 0.07,
    'ETH-UNI': 150,
    'ETH-LINK': 50,
    'USDT-ETH': 0.00033,
    'USDT-USDC': 1,
    'USDT-DAI': 1,
    'USDT-WBTC': 0.000023,
    'USDT-UNI': 0.05,
    'USDT-LINK': 0.016,
    'SOL-USDC': 100,
    'SOL-RAY': 10,
    'SOL-SRM': 20,
    'SOL-ORCA': 15,
    'TON-JETTON': 10,
    'TON-BOLT': 20
  };

  // Update exchange rate when tokens change
  useEffect(() => {
    const key = `${fromToken}-${toToken}`;
    const inverseKey = `${toToken}-${fromToken}`;
    
    if (exchangeRates[key]) {
      setExchangeRate(exchangeRates[key]);
    } else if (exchangeRates[inverseKey]) {
      setExchangeRate(1 / exchangeRates[inverseKey]);
    } else {
      setExchangeRate(1);
    }
  }, [fromToken, toToken]);

  // Update to amount when from amount or exchange rate changes
  useEffect(() => {
    if (fromAmount && exchangeRate) {
      const calculated = parseFloat(fromAmount) * exchangeRate;
      setToAmount(calculated.toFixed(6));
    } else {
      setToAmount('');
    }
  }, [fromAmount, exchangeRate]);

  // Reset form
  const resetForm = () => {
    setFromToken('ETH');
    setToToken('USDT');
    setFromAmount('');
    setToAmount('');
    setSlippage(0.5);
    setError('');
    setShowSettings(false);
    setStep(1);
  };

  // Handle close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Swap tokens
  const handleSwapTokens = () => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    
    if (fromAmount) {
      setFromAmount(toAmount);
    }
  };

  // Validate form
  const validateForm = () => {
    if (!fromAmount || isNaN(parseFloat(fromAmount)) || parseFloat(fromAmount) <= 0) {
      setError('Please enter a valid amount to swap');
      return false;
    }

    // Check if amount is greater than balance
    const balance = parseFloat(tokenBalances[fromToken] || '0');
    if (parseFloat(fromAmount) > balance) {
      setError(`Insufficient ${fromToken} balance. You have ${balance} ${fromToken}`);
      return false;
    }

    if (fromToken === toToken) {
      setError('Cannot swap a token for itself');
      return false;
    }

    setError('');
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (validateForm()) {
      setStep(2);
    }
  };

  // Handle swap
  const handleSwap = async () => {
    try {
      setLoading(true);
      setError('');

      // Simulate transaction
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Call the onSwap callback with transaction details
      onSwap({
        fromToken,
        toToken,
        fromAmount,
        toAmount,
        rate: exchangeRate,
        slippage,
        timestamp: new Date().toISOString()
      });

      setStep(3);
      setLoading(false);
    } catch (error) {
      console.error('Error swapping tokens:', error);
      setError(error.message || 'Failed to swap tokens');
      setLoading(false);
    }
  };

  // Calculate price impact
  const calculatePriceImpact = () => {
    // Mock calculation - in a real app this would be more complex
    const amount = parseFloat(fromAmount || '0');
    if (amount < 1) return 0.1;
    if (amount < 10) return 0.5;
    if (amount < 100) return 1.5;
    return 3;
  };

  // Calculate gas fee
  const calculateGasFee = () => {
    // Mock calculation - in a real app this would be based on current gas prices
    return '0.005 ETH';
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 10
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'info.main' }}>
            <SwapHoriz />
          </Avatar>
          <Typography variant="h6">
            {step === 1 ? 'Swap Tokens' : step === 2 ? 'Confirm Swap' : 'Swap Successful'}
          </Typography>
        </Box>
        <Box>
          {step === 1 && (
            <Tooltip title="Swap Settings">
              <IconButton onClick={() => setShowSettings(!showSettings)} sx={{ mr: 1 }}>
                <Settings />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={handleClose} disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {step === 1 && (
          <>
            {showSettings && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Slippage Tolerance
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Slider
                    value={slippage}
                    onChange={(e, newValue) => setSlippage(newValue)}
                    step={0.1}
                    min={0.1}
                    max={5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ minWidth: 40 }}>
                    {slippage}%
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Your transaction will revert if the price changes unfavorably by more than this percentage.
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">From</Typography>
                <Typography variant="caption" color="text.secondary">
                  Balance: {tokenBalances[fromToken] || '0'} {fromToken}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                bgcolor: 'rgba(0, 0, 0, 0.02)', 
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                p: 2
              }}>
                <TextField
                  fullWidth
                  value={fromAmount}
                  onChange={(e) => setFromAmount(e.target.value)}
                  type="number"
                  placeholder="0.00"
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    inputProps: { min: 0, step: 0.000001 }
                  }}
                  sx={{ input: { fontSize: '1.2rem', fontWeight: 'medium' } }}
                />
                <FormControl sx={{ minWidth: 100 }}>
                  <Select
                    value={fromToken}
                    onChange={(e) => setFromToken(e.target.value)}
                    variant="standard"
                    disableUnderline
                    sx={{ fontSize: '1.1rem' }}
                  >
                    {availableTokens.map((token) => (
                      <MenuItem key={token} value={token}>{token}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
              <IconButton 
                onClick={handleSwapTokens}
                sx={{ 
                  bgcolor: 'background.paper', 
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'primary.light', color: 'white' }
                }}
              >
                <SwapVert />
              </IconButton>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">To (Estimated)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Balance: {tokenBalances[toToken] || '0'} {toToken}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                bgcolor: 'rgba(0, 0, 0, 0.02)', 
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
                p: 2
              }}>
                <TextField
                  fullWidth
                  value={toAmount}
                  placeholder="0.00"
                  variant="standard"
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true
                  }}
                  sx={{ input: { fontSize: '1.2rem', fontWeight: 'medium' } }}
                />
                <FormControl sx={{ minWidth: 100 }}>
                  <Select
                    value={toToken}
                    onChange={(e) => setToToken(e.target.value)}
                    variant="standard"
                    disableUnderline
                    sx={{ fontSize: '1.1rem' }}
                  >
                    {availableTokens.map((token) => (
                      <MenuItem key={token} value={token}>{token}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {fromAmount && toAmount && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1
              }}>
                <Typography variant="body2" color="text.secondary">
                  Rate
                </Typography>
                <Typography variant="body2">
                  1 {fromToken} = {exchangeRate} {toToken}
                </Typography>
              </Box>
            )}
          </>
        )}

        {step === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review your swap details before confirming.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                p: 3,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                    {fromToken.charAt(0)}
                  </Avatar>
                  <Typography variant="h6">{fromAmount} {fromToken}</Typography>
                </Box>
                
                <ArrowDownward sx={{ my: 1, color: 'text.secondary' }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', mr: 1 }}>
                    {toToken.charAt(0)}
                  </Avatar>
                  <Typography variant="h6">{toAmount} {toToken}</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Swap Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Rate</Typography>
                  <Typography variant="body2">1 {fromToken} = {exchangeRate} {toToken}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Price Impact
                    <Tooltip title="The difference between the market price and estimated price due to trade size.">
                      <Info fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                    </Tooltip>
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={calculatePriceImpact() > 2 ? 'warning.main' : 'success.main'}
                  >
                    {calculatePriceImpact()}%
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Slippage Tolerance</Typography>
                  <Typography variant="body2">{slippage}%</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Network Fee</Typography>
                  <Typography variant="body2">{calculateGasFee()}</Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Minimum Received</Typography>
                  <Typography variant="subtitle2">
                    {(parseFloat(toAmount) * (1 - slippage / 100)).toFixed(6)} {toToken}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {step === 3 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Avatar sx={{ bgcolor: 'success.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
              <SwapHoriz sx={{ fontSize: 30 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom>Swap Successful!</Typography>
            <Typography variant="body1" paragraph>
              You have successfully swapped {fromAmount} {fromToken} for {toAmount} {toToken}.
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(0, 0, 0, 0.02)', 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mb: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Transaction Hash</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {`0x${Math.random().toString(36).substring(2, 10)}...${Math.random().toString(36).substring(2, 10)}`}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Time</Typography>
                <Typography variant="body2">
                  {new Date().toLocaleTimeString()}
                </Typography>
              </Box>
            </Box>
            
            <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
              Your new {toToken} balance: {(parseFloat(tokenBalances[toToken] || '0') + parseFloat(toAmount)).toFixed(4)} {toToken}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 1 && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleNext}
            disabled={!fromAmount || !toAmount || loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Review Swap'}
          </Button>
        )}

        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)} disabled={loading} sx={{ flex: 1 }}>
              Back
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSwap}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Confirm Swap'}
            </Button>
          </>
        )}

        {step === 3 && (
          <Button variant="contained" onClick={handleClose} fullWidth>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SwapDialog;
